import type { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { mealsApi } from '../../../api/meals.api';
import { toast } from '../../ui/Toast';
import type { ItineraryDay, Meal } from '../../../types';
import type { MealStopActions } from './MealStopLink';

/**
 * Linking a meal to a stop, or making one (ADR-0024). Both change the
 * itinerary too — a new stop, or a meal that took a stop's title — so the
 * trip's days are reloaded after each.
 */
export function useMealStops(
  tripId: string,
  days: ItineraryDay[],
  setMeals: Dispatch<SetStateAction<Meal[]>>,
  loadDays: () => Promise<void>,
): MealStopActions {
  const { t } = useTranslation();

  async function apply(request: Promise<Meal>) {
    try {
      const saved = await request;
      setMeals(prev => prev.map(m => (m.id === saved.id ? saved : m)));
      await loadDays();
    } catch {
      toast(t('meals.toast.stopFailed'), 'error');
    }
  }

  return {
    days,
    onLink: (meal, activityId) => apply(mealsApi.linkActivity(tripId, meal.id, activityId)),
    onCreate: (meal, startTime) => apply(mealsApi.createActivity(tripId, meal.id, startTime)),
  };
}
